import React, { Component } from 'react';
import { connect, ConnectedProps } from 'react-redux';
// TODO: Lodash should no longer be used here
// eslint-disable-next-line no-restricted-imports
import _ from 'lodash';
import { withTranslation, WithTranslation } from 'react-i18next';
import { NavigateFunction, Params, useLocation, useNavigate, useParams } from 'react-router-dom';
// eslint-disable-next-line no-restricted-imports
import { Form, Header, Segment } from 'semantic-ui-react';

// Import actions
import { completeUserSignup } from '../../../redux/actions/userActions';

// Import Components
import LocationTypePicker from '../../Locations/LocationTypePicker';
import InlineError from '../../Messages/InlineError';
import ShowErrors from '../../Messages/ShowError';
import Disclaimer from '../../Shared/Disclaimer';

import { routes } from '../../../config/routes';

// Type definitions
interface SignupData {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
}

interface FormData {
  organizationName: string;
  programType: string;
  enabled: boolean;
  language: string;
}

interface ComponentState {
  data: FormData;
  loading: boolean;
  errors: Record<string, string>;
}

interface RouterProps {
  navigate: NavigateFunction;
  params: Params;
  matchPath: string;
}

interface OwnProps extends WithTranslation {
  signupData: SignupData;
  networkId?: string;
}

// Redux connector
const mapStateToProps = () => ({});
const mapDispatchToProps = {
  completeUserSignup,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = PropsFromRedux & OwnProps & RouterProps;

class CompleteSignupForm extends Component<Props, ComponentState> {
  state: ComponentState = {
    data: {
      organizationName: '',
      programType: '',
      enabled: true,
      language: this.props.i18n.language,
    },
    loading: false,
    errors: {},
  };

  onChange = (_e: any, { name, value }: any) => {
    this.setState({
      data: { ...this.state.data, [name]: value },
      errors: _.omit(this.state.errors, name),
    });
  };

  onSubmit = async (e: React.FormEvent) => {
    const { navigate, signupData, networkId } = this.props;
    e.preventDefault();
    const errors = this.validate(this.state.data);
    this.setState({ errors });

    if (_.isEmpty(errors)) {
      this.setState({ loading: true });

      const { firstName, lastName, email, phone } = signupData;

      const isFirstOrgUser = true; // this is always true because all other students/staff are invited with a pre-created account via the reset password flow

      try {
        await this.props.completeUserSignup(
          {
            firstName,
            lastName,
            email,
            phone,
            networkId,
            ...this.state.data, // form values override preset in case signupData is missing
          },
          isFirstOrgUser
        );
        navigate(routes.verify, { replace: true });
      } catch (error: any) {
        this.setState({
          errors: { 'Sign Up Error': error.message },
          loading: false,
        });
      }
    }
  };

  validate = (data: FormData): Record<string, string> => {
    const { t } = this.props;
    const errors: Record<string, string> = {};

    if (!data.organizationName) errors.organizationName = t('auth.completeSignup.validation.programNameRequired');
    if (!data.programType) errors.programType = t('auth.completeSignup.validation.programTypeRequired');
    return errors;
  };

  isSignUpRoute = (): boolean => {
    const { matchPath } = this.props;
    return matchPath === routes.signup;
  };

  render() {
    const { data, errors, loading } = this.state;
    const { t, signupData } = this.props;

    return (
      <Segment basic textAlign="left">
        <Header as="h1" textAlign="center" content={t('auth.completeSignup.heading')} />

        <br />

        <ShowErrors errors={errors} />

        <Form onSubmit={this.onSubmit} loading={loading} noValidate>
          <Form.Group widths="equal">
            <Form.Field hidden={!!signupData.firstName}>
              <Form.Input
                required
                fluid
                type="text"
                id="firstName"
                name="firstName"
                label={t('common.firstName')}
                placeholder={t('common.firstName')}
                value={signupData.firstName || ''}
              />
            </Form.Field>

            <Form.Field hidden={!!signupData.firstName}>
              <Form.Input
                required
                fluid
                type="text"
                id="lastName"
                name="lastName"
                label={t('common.lastName')}
                placeholder={t('common.lastName')}
                value={signupData.lastName || ''}
              />
            </Form.Field>
          </Form.Group>
          <Form.Field hidden={!!signupData.email}>
            <Form.Input
              required
              fluid
              type="email"
              id="email"
              name="email"
              label={t('common.email')}
              value={signupData.email || ''}
              autoComplete="username"
            />
          </Form.Field>
          <Form.Field hidden={!!signupData.email}>
            <Form.Input
              required
              fluid
              type="phone"
              id="phone"
              name="phone"
              label={t('common.phone')}
              value={signupData.phone || ''}
            />
          </Form.Field>
          <Form.Field error={!!errors.organizationName}>
            <Form.Input
              required
              fluid
              type="text"
              id="organizationName"
              name="organizationName"
              label={t('auth.completeSignup.programNameLabel')}
              placeholder={t('auth.completeSignup.programNamePlaceholder')}
              value={data.organizationName}
              onChange={this.onChange}
            />
            {errors.organizationName && <InlineError text={errors.organizationName} />}
          </Form.Field>
          <Form.Field error={!!errors.programType}>
            <Form.Field
              translator={t}
              isForm
              required
              id="programType"
              name="programType"
              label={t('auth.completeSignup.programTypeLabel')}
              control={LocationTypePicker}
              placeholder={t('auth.completeSignup.programTypePlaceholder')}
              value={data.programType}
              selection
              search
              onChange={this.onChange}
            />
            {errors.programType && <InlineError text={errors.programType} />}
          </Form.Field>
          <Form.Button fluid size="large" primary content={t('auth.completeSignup.submitAction')} />
          <Form.Field>
            <Disclaimer type="terms" />
          </Form.Field>
        </Form>
      </Segment>
    );
  }
}

// HOC wrapper to inject router hooks
function withRouterHooks<T extends object>(Component: React.ComponentType<T & RouterProps>) {
  return function WrappedComponent(props: T) {
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();
    const matchPath = location.pathname;

    return <Component {...props} navigate={navigate} params={params} matchPath={matchPath} />;
  };
}

export default withTranslation()(withRouterHooks(connector(CompleteSignupForm)));
